import { DateTime } from "luxon";
import { ACTIVITY_CHANGE } from "./activityConstants";

export const formatActivityTime = (dateValue: Date): string => {
  const dt = new Date(dateValue);
  let hours = dt.getHours();
  const minutes = String(dt.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm}`;
};

export const activityEntryDescription = (
  oldValue: any,
  newValue: any,
): {
  type: ACTIVITY_CHANGE;
  oldVal?: string;
  newVal?: string;
} => {
  const oldStr = String(oldValue ?? "").trim();
  const newStr = String(newValue ?? "").trim();

  if (oldStr === "" && newStr !== "") {
    return { type: ACTIVITY_CHANGE.Added, newVal: newStr };
  }
  if (oldStr !== "" && newStr === "") {
    return { type: ACTIVITY_CHANGE.Removed, oldVal: oldStr };
  }
  return { type: ACTIVITY_CHANGE.Changed, oldVal: oldStr, newVal: newStr };
};

export const formatTextOrDate = (value?: string): string | undefined => {
  if (!value) return value;

  const datePatterns = [
    /^\d{4}-\d{2}-\d{2}/, // ISO date (YYYY-MM-DD)
    /^\d{1,2}\/\d{1,2}\/\d{2,4}/, // M/D/YYYY or MM/DD/YYYY
    /^\d{1,2}-\d{1,2}-\d{2,4}/, // M-D-YYYY or MM-DD-YYYY
    /^\d{4}\/\d{2}\/\d{2}/, // YYYY/MM/DD
  ];

  const looksLikeDate = datePatterns.some(pattern => pattern.test(value.trim()));

  if (!looksLikeDate) {
    return value;
  }

  try {
    const dt = DateTime.fromISO(value, { zone: "utc" }).isValid
      ? DateTime.fromISO(value, { zone: "utc" })
      : DateTime.fromFormat(value.trim(), "M/d/yyyy", { zone: "utc" }).isValid
        ? DateTime.fromFormat(value.trim(), "M/d/yyyy", { zone: "utc" })
        : DateTime.fromFormat(value.trim(), "M-d-yyyy", { zone: "utc" });

    if (!dt.isValid) return value;

    return dt.toFormat("MMMM d, yyyy");
  } catch {
    return value;
  }
};

export const getDateLabel = (dateValue: Date): string => {
  const now = DateTime.now();
  const dt = DateTime.fromJSDate(dateValue);
  const diffDays = now.startOf("day").diff(dt.startOf("day"), "days").days;
  const baseFormat = dt.toFormat("MMMM d, yyyy");

  if (diffDays === 0) return `${baseFormat} Today`;
  if (diffDays === 1) return `${baseFormat} Yesterday`;
  return baseFormat;
};

export interface ActivityWithDate {
  id?: string;
  changedAt?: Date;
  [key: string]: any;
}

export const groupActivitiesByDate = <T extends ActivityWithDate>(activities: T[]) => {
  const grouped = activities.reduce(
    (acc: Record<string, T[]>, activity: T) => {
      if (!activity.changedAt) return acc;
      const dateKey = DateTime.fromJSDate(activity.changedAt).toFormat(
        "yyyy-MM-dd",
      );
      acc[dateKey] ??= [];
      acc[dateKey].push(activity);
      return acc;
    },
    {} as Record<string, T[]>,
  );

  return {
    grouped,
    sortedDates: Object.keys(grouped).sort((a, b) => b.localeCompare(a)),
  };
};
